/**
 * Public slot-model reads and the free-listing sign-up (Phase 5c, SLOT_MODEL flag).
 * Goes through the patron client `api` (no login needed; a stored patron token is
 * attached as on every other public call and ignored by these routes).
 *
 *   GET  /slots/tiers                  [TierCatalogItem]  (backend/api/schemas/slots.py)
 *   GET  /venues/availability          [VenueAvailability]
 *   GET  /venues/{id}/availability     VenueAvailability (404 unknown/archived venue)
 *   GET  /locations                    [Location] every active venue, one query, not flag-gated
 *                                      (backend/api/routers/public_venues.py: the locations row
 *                                      minus admin-only fields; `id`, `name`, `city`, `state`,
 *                                      `archived_at`, ...). The patron venue picker reads it.
 *   GET  /categories                   { categories: [{ id, name, slug, children: [{ id, name, slug }] }] }
 *   POST /listings/free                201 { business_id, slug, verification_required }
 *                                      409 email_taken · 422 category_invalid | venues_invalid · 429
 *                                      202 { status: "set_password_sent" } for a guest's email (7b)
 *   POST /listings/free/me             the one SIGNED-IN call here (Phase 7b): same body minus email
 *                                      and password, the patron token identifies the owner →
 *                                      201 { business_id, slug, verification_required }
 *                                      409 business_exists · 403 admin_account · 422 category_invalid |
 *                                      venues_invalid | validation · 429 · 500 listing_failed
 *                                      It sits next to createFreeListing (same page, same body);
 *                                      the patron client already attaches the stored token.
 *
 * Phase 6c (venue deals page + scan page; backend/api/routers/venue_deals.py, patron.py):
 *   GET  /venues/{id}/deals-page[?category=<slug>]  VenueDealsPage { venue, sponsor|null, premium[],
 *                                      standard[], free_listings[], categories[], category,
 *                                      generated_at } (404 unknown/archived/inactive venue)
 *   POST /scan/record                  { id, design_id, deduplicated?, ... } (recordScan; body from
 *                                      publicData.scanRecordBody; not flag-gated)
 *   POST /businesses/{id}/view         201 { recorded, location_id, design_id, page_id }
 *                                      (404 unknown business, 429 over 120/minute)
 *
 * With SLOT_MODEL off every one of these paths answers 404 (hidden_by_flag).
 * Money on the wire is integer cents (monthly_price_cents, quarterly_price_cents,
 * annual_price_cents); see lib/slotPricing.js for formatting and the discount maths.
 */
import { qs } from './client';
import { api } from './patron';
import { businessViewBody, scanRecordBody } from './publicData';

export const getSlotTiers = (config) => api.get('/slots/tiers', config);
export const getVenuesAvailability = (config) => api.get('/venues/availability', config);
export const getVenueAvailability = (locationId, config) =>
  api.get(`/venues/${encodeURIComponent(locationId)}/availability`, config);
export const getCategories = (config) => api.get('/categories', config);
/**
 * Every active venue (GET /locations; public, not flag-gated). The venue picker on the deals
 * and scan pages uses it: the deals page serves every live venue, not only the ones that
 * joined the slot model, so the picker must not read /venues/availability.
 */
export const getPublicLocations = (config) => api.get('/locations', config);

/**
 * payload: { email, password, business_name, category_id, phone?, website?, address?,
 * city?, state?, offer: { title, description, terms? }, venue_ids: [up to 3] }
 */
export const createFreeListing = (payload, config) => api.post('/listings/free', payload, config);
/**
 * Free listing for the signed-in user (a guest who set a password finishes here). payload =
 * the createFreeListing body without email / password.
 */
export const createMyFreeListing = (payload, config) => api.post('/listings/free/me', payload, config);

// ---- Email-only claims + the signed wallet link (Phase 6c, U3; SLOT_MODEL) ----
//   POST /claims/email    { email, deal_id, hub_id?, design_id?, session_id?, honeypot? }
//                         201 { email, existing: false, email_sent, claim_id, code, expires_at,
//                               deal: { id, title, business_name, business_slug }, wallet_url }
//                         200 { existing: true, email_sent, claim_id, expires_at, deal, wallet_url } (no code)
//                         404 deal_unavailable · 409 claim_unavailable | claim_limit |
//                         claim_conflict · 422 · 429
//   GET  /wallet          X-Wallet-Token: <token>  (the header only; never ?t= from here)
//                         200 { email, active_count, max_active, link_expires_at, claims: [my-claims item] }
//                         401 wallet_link_invalid | wallet_link_expired | wallet_link_revoked
//   POST /wallet/resend   { email, honeypot? } → always 200 { status: "ok", message }
// The wallet token is a bearer credential. It travels in the X-Wallet-Token header, so it
// stays out of request URLs (access logs, proxies); the email link carries it in the URL
// fragment (#t=, never sent to a server) and the page strips it from the address bar
// (pages/public/Wallet.jsx).
export const claimByEmail = (payload, config) => api.post('/claims/email', payload, config);

export const WALLET_TOKEN_HEADER = 'X-Wallet-Token';

/** GET /wallet with the token in the X-Wallet-Token header: one request, no token in the URL. */
export const getWalletByToken = (t, config = {}) =>
  api.get('/wallet', { ...config, headers: { ...(config.headers || {}), [WALLET_TOKEN_HEADER]: t } });
export const resendWalletLink = (email, config) => api.post('/wallet/resend', { email }, config);

/**
 * The venue's public deals page, already in display order (Sponsor, Premium, Standard, free
 * listings). `category` = a subcategory or top-level category slug; empty = unfiltered.
 */
export const getVenueDealsPage = (locationId, { category } = {}, config) =>
  api.get(`/venues/${encodeURIComponent(locationId)}/deals-page${qs({ category: category || undefined })}`, config);

/**
 * Record a QR scan / scan-page visit (not flag-gated). `payload` = { hub_id | location_id,
 * session_id, d?, referral_source?, campaign_id?, user_id? }: today's ScanNew fields plus
 * the design token (see publicData.scanRecordBody). Moved here from lib/api/patron.
 */
export const recordScan = (payload, config) => api.post('/scan/record', scanRecordBody(payload), config);

/**
 * Record one view of a business: `{ source?, location_id?, design_id?, page_id? }`; only the
 * fields that are set are sent (see publicData.businessViewBody).
 */
export const recordBusinessView = (businessId, attribution = {}, config) =>
  api.post(`/businesses/${encodeURIComponent(businessId)}/view`, businessViewBody(attribution), config);

// Pure helpers for these responses live in ./publicData (no network), so a test that mocks
// this module's calls keeps them; they are re-exported here as part of the public API.
export {
  publicErrorCode, unwrap, toVenueList, scanRecordBody, businessViewBody, httpStatus,
} from './publicData';

// ---- Advertiser pages + page leads (Phase 6c, U2; SLOT_MODEL) ----
//   GET  /pages/{business_slug}              AdvertiserPageResponse: the Premium landing page,
//                                            or a Venue Sponsor's first microsite page
//   GET  /pages/{business_slug}/{page_slug}  one microsite page
//        200 { business: { id, name, slug, logo_url, subcategory, tier: premium | sponsor },
//              page: { id, slug, kind: landing | microsite, title, content (v1), published_at },
//              nav: [{ slug, title }], deal: PublicDeal | null, lead_form_enabled }
//        404 { detail: { error: "page_not_found" } } (no live Premium/Sponsor slot, no page)
//   POST /leads  { business_slug, page_slug?, name, email, phone?, message?, consent: true, honeypot? }
//        201 { lead_id, message, existing: false } · 200 { lead_id: null, message, existing: false }
//        for a honeypot hit or a repeat within 24 h (nothing stored; `existing` is always false)
//        404 page_not_found · 409 lead_form_disabled · 422 · 429 (10 an hour per IP)
// Content v1 is backend/api/schemas/pages.py; pages/public/BusinessPage.jsx renders it.
export const getAdvertiserPage = (businessSlug, pageSlug, config) => {
  const base = `/pages/${encodeURIComponent(businessSlug)}`;
  return api.get(pageSlug ? `${base}/${encodeURIComponent(pageSlug)}` : base, config);
};
export const submitLead = (payload, config) => api.post('/leads', payload, config);
